import {Badge, Box, Grid} from '@mui/material';
import {FilterType, SortTypeEnum} from 'api/common';
import {AppIconButton} from 'components/buttons';
import {AppDrawer} from 'components/drawers';
import {AppTooltip} from 'components/toolip';
import {
    useChangeNotificationsStatus,
    useGetAllNotifications,
    useGetUnreadNotificationsCount,
} from 'hooks/useNotification';
import {NotificationModel, NotificationStatus} from 'models';
import {useCallback, useEffect, useState} from 'react';
import {MdMarkEmailUnread, MdNotificationsNone} from 'react-icons/md';
import {RiCheckDoubleFill, RiListCheck} from 'react-icons/ri';
import InfiniteScroll from 'react-infinite-scroll-component';
import {useSelector} from 'react-redux';
import {AppRootState} from 'store/appStore';
import {sessionSelector} from 'store/auth/sessionSlice';
import {appDrawerSelector} from 'store/themeSettings';
import {DrawerTypeValue} from 'types';
import NotificationCard from './components/NotificationCard';
export const NotificationDrawer = () => {
    const refreshKey = useSelector((state: AppRootState) => state.notification.refreshKey);
    const {fetchedNotifications, notificationLoading, notificationResponse} = useGetAllNotifications();
    const {changeNotificationsStatus, changeNotificationsStatusLoading, changeNotificationsStatusResponse} =
        useChangeNotificationsStatus();
    const {fetchedUnreadNotificationsCount, unreadNotificationsCount} = useGetUnreadNotificationsCount();
    const [updatedIds, setUpdatedIds] = useState<string[]>([]);
    const {isRightDrawerOpened} = useSelector(appDrawerSelector);
    const {userId} = useSelector(sessionSelector);

    const [notifications, setNotifications] = useState<NotificationModel[]>([]);
    const [showAll, setShowAll] = useState<boolean>(false);
    // Methods
    const generateWhereCondition = useCallback((): FilterType<NotificationModel> | undefined => {
        const andFilters: any[] = [];
        if (userId) {
            andFilters.push({userId: {eq: userId}});
        }
        if (!showAll) {
            andFilters.push({status: {eq: NotificationStatus.UNREAD}});
        }
        return andFilters.length > 0 ? {and: andFilters} : undefined;
    }, [userId, showAll]);
    const appendNotificationsDataUnique = useCallback((value: NotificationModel[]) => {
        setNotifications(prev => {
            if (prev) {
                const ids = prev.map(item => item.id);
                const newReminder = value.filter(item => !ids.includes(item.id));
                return [...prev, ...newReminder];
            }
            return value;
        });
    }, []);
    const handleNext = () => {
        fetchedNotifications({
            variables: {
                first: 12,
                after: notificationResponse?.pageInfo.endCursor,
                order: [
                    {
                        createdDate: SortTypeEnum.DESC,
                    },
                ],
                where: generateWhereCondition(),
            },
        });
    };
    const handleMarkAsRead = (ids: string[]) => {
        changeNotificationsStatus({
            variables: {
                ids,
                status: NotificationStatus.READ,
            },
        });

        setUpdatedIds(ids);
    };
    // UseEffects
    useEffect(() => {
        setNotifications([]);
        fetchedUnreadNotificationsCount({
            variables: {
                userId: userId ?? '',
            },
        });
        fetchedNotifications({
            variables: {
                first: 12,
                order: [
                    {
                        createdDate: SortTypeEnum.DESC,
                    },
                ],
                where: {
                    and: [
                        ...(userId ? [{userId: {eq: userId}}] : []),
                        {status: {eq: NotificationStatus.UNREAD}},
                    ] as any,
                },
            },
        });
    }, [userId]);
    useEffect(() => {
        if (!notificationLoading && notificationResponse) {
            appendNotificationsDataUnique(notificationResponse.nodes);
        }
    }, [notificationResponse, showAll]);

    // Use notifications state as the data source for InfiniteScroll
    useEffect(() => {
        if (refreshKey > 0) {
            setNotifications([]);
            setUpdatedIds([]);
            fetchedUnreadNotificationsCount({
                variables: {
                    userId: userId ?? '',
                },
            });
            fetchedNotifications({
                variables: {
                    first: 12,
                    order: [
                        {
                            createdDate: SortTypeEnum.DESC,
                        },
                    ],
                    where: {
                        and: [
                            ...(userId ? [{userId: {eq: userId}}] : []),
                            {status: {eq: NotificationStatus.UNREAD}},
                        ] as any,
                    },
                },
            });
        }
    }, [refreshKey]);

    useEffect(() => {
        if (!changeNotificationsStatusLoading && changeNotificationsStatusResponse) {
            setNotifications(prev =>
                prev.map(notification =>
                    updatedIds.includes(notification.id)
                        ? {...notification, status: NotificationStatus.READ}
                        : notification,
                ),
            );

            setUpdatedIds([]);
            // Refresh notifications after marking as read
            setNotifications([]);
            fetchedUnreadNotificationsCount({
                variables: {
                    userId: userId ?? '',
                },
            });
            fetchedNotifications({
                variables: {
                    first: 12,
                    order: [
                        {
                            createdDate: SortTypeEnum.DESC,
                        },
                    ],
                    where: {
                        and: [
                            ...(userId ? [{userId: {eq: userId}}] : []),
                            ...(!showAll ? [{status: {eq: NotificationStatus.UNREAD}}] : []),
                        ] as any,
                    },
                },
            });
        }
    }, [changeNotificationsStatusResponse, changeNotificationsStatusLoading]);
    // Show all notifications handler
    const handleShowAll = () => {
        setNotifications([]);
        setShowAll(true);
        fetchedNotifications({
            variables: {
                first: 12,
                order: [
                    {
                        createdDate: SortTypeEnum.DESC,
                    },
                ],
                where: {
                    and: [...(userId ? [{userId: {eq: userId}}] : [])] as any,
                },
            },
        });
    };

    const handleShowUnreadNotifications = () => {
        setNotifications([]);
        setShowAll(false);
        fetchedNotifications({
            variables: {
                first: 12,
                order: [
                    {
                        createdDate: SortTypeEnum.DESC,
                    },
                ],
                where: {
                    and: [
                        ...(userId ? [{userId: {eq: userId}}] : []),
                        {status: {eq: NotificationStatus.UNREAD}},
                    ] as any,
                },
            },
        });
    };

    return (
        <>
            <AppDrawer
                drawerType={DrawerTypeValue.Notification}
                title="Notifications"
                showHeaderButton
                renderRightHeader={
                    <Box display="flex" alignItems="center" gap={1}>
                        {!showAll && unreadNotificationsCount > 0 && (
                            <AppTooltip title="Mark all as Read">
                                <AppIconButton
                                    onClick={() => {
                                        const ids = notifications
                                            .filter(x => x.status === NotificationStatus.UNREAD)
                                            .map(x => x.id);
                                        handleMarkAsRead(ids);
                                    }}
                                    variant="outlined"
                                    color="success"
                                >
                                    <RiCheckDoubleFill />
                                </AppIconButton>
                            </AppTooltip>
                        )}
                        {!showAll && (
                            <AppTooltip title="Show All Notifications">
                                <AppIconButton onClick={handleShowAll} variant="outlined" color="primary">
                                    <RiListCheck />
                                </AppIconButton>
                            </AppTooltip>
                        )}
                        {showAll && (
                            <AppTooltip title="Show Unread Notifications">
                                <AppIconButton
                                    onClick={handleShowUnreadNotifications}
                                    variant="outlined"
                                    color="primary"
                                >
                                    <MdMarkEmailUnread />
                                </AppIconButton>
                            </AppTooltip>
                        )}
                    </Box>
                }
                renderElement={(_open, handleOpen) => {
                    return (
                        <Box sx={{flexShrink: 0, ml: 0.75}}>
                            <AppTooltip title="Notifications" placement="left-start">
                                <AppIconButton
                                    elevation={DrawerTypeValue.Notification === isRightDrawerOpened}
                                    onClick={handleOpen}
                                >
                                    <Badge
                                        badgeContent={unreadNotificationsCount}
                                        color="primary"
                                        sx={{
                                            '& .MuiBadge-badge': {
                                                fontSize: '0.6rem',
                                                height: '13px',
                                                minWidth: '13px',
                                                padding: '0 4px',
                                            },
                                        }}
                                    >
                                        <MdNotificationsNone />
                                    </Badge>
                                </AppIconButton>
                            </AppTooltip>
                        </Box>
                    );
                }}
            >
                <Box pt={2}>
                    {notifications.length === 0 && !notificationLoading ? (
                        <Box width="100%" textAlign="center" mt={6} color="#8a8a8a">
                            <MdNotificationsNone size={48} style={{opacity: 0.3}} />
                            <Box mt={1} fontSize={18} fontWeight={500}>
                                No notifications
                            </Box>
                            <Box fontSize={14} color="#b0b0b0">
                                You're all caught up!
                            </Box>
                        </Box>
                    ) : (
                        <InfiniteScroll
                            height={'calc(100vh - 120px)'}
                            style={{
                                scrollbarWidth: 'thin',
                                scrollbarColor: 'rgb(203, 211, 222) transparent',
                                marginTop: '10px',
                            }}
                            scrollableTarget="scrollableDiv"
                            dataLength={notifications.length}
                            next={handleNext}
                            hasMore={notificationResponse?.pageInfo.hasNextPage ?? false}
                            loader={
                                <Box textAlign="center" color="#8a8a8a" mt={2}>
                                    Loading more notifications...
                                </Box>
                            }
                        >
                            <Grid container>
                                {notifications.map((notification: NotificationModel) => (
                                    <NotificationCard
                                        key={notification.id}
                                        notification={notification}
                                        handleMarkAsRead={handleMarkAsRead}
                                    />
                                ))}
                            </Grid>
                        </InfiniteScroll>
                    )}
                </Box>
            </AppDrawer>
        </>
    );
};

export default NotificationDrawer;
